import { localeEn } from "./locales/en";
import { localeJa } from "./locales/ja";

interface ValidationError {
    errored: unknown;
    code: string;
    label?: string;
}

export type Locale = "en" | "ja";

const errorMsg: Record<Locale, typeof localeEn> = {
    en: localeEn,
    ja: localeJa,
};

export class Validator {
    private locale: Locale = "en";
    public inputValue: unknown;
    private currentErrorName = "";
    public errorMap: Record<string, ValidationError[]> = {};
    private pristineMap: Record<string, boolean> = {};
    private valueMap: Record<string, unknown> = {};

    public setLocale(locale: Locale): void {
        this.locale = locale;
    }

    public value(inputValue: unknown): this {
        this.inputValue = inputValue;
        return this;
    }

    /**
     * Name the current value for grouping errors in errorMap.
     * Must be chained after value().
     */
    public as(inputName: string): this {
        this.currentErrorName = inputName;
        if (this.pristineMap[inputName] === undefined) {
            this.pristineMap[inputName] = true;
        } else if (this.valueMap[inputName] !== this.inputValue) {
            this.pristineMap[inputName] = false;
        }
        this.errorMap[inputName] = [];
        this.valueMap[inputName] = this.inputValue;
        return this;
    }

    private _addError(err: ValidationError): void {
        if (!this.currentErrorName) return;
        if (this.pristineMap[this.currentErrorName]) return;
        if (!this.errorMap[this.currentErrorName])
            this.errorMap[this.currentErrorName] = [];
        this.errorMap[this.currentErrorName].push(err);
    }

    // Static type guards
    public static isDefined = (x: unknown): boolean => x !== undefined && x !== null;
    public static isUndefined = (x: unknown): boolean => x === undefined;
    public static isArray = Array.isArray;
    public static isString = (x: unknown): boolean => typeof x === "string" || x instanceof String;
    public static isObject = (x: unknown): boolean => Object.prototype.toString.call(x) === "[object Object]";
    public static isNumber = (x: unknown): boolean => Object.prototype.toString.call(x) === "[object Number]";
    public static isBoolean = (x: unknown): boolean => Object.prototype.toString.call(x) === "[object Boolean]";

    /**
     * Check minimum length (strings/arrays) or minimum value (numbers).
     * Does not check for null/undefined — use required() for that.
     */
    public min(min: number): this {
        if (!this.inputValue) return this;
        if (Validator.isArray(this.inputValue) || Validator.isString(this.inputValue)) {
            if ((this.inputValue as string | unknown[]).length < min)
                this._addError({ errored: this.inputValue, code: "MinReachedS", label: errorMsg[this.locale].min(String(min)) });
        } else if (Validator.isNumber(this.inputValue)) {
            if ((this.inputValue as number) < min)
                this._addError({ errored: this.inputValue, code: "MinReachedN", label: errorMsg[this.locale].min(String(min)) });
        }
        return this;
    }

    public max(max: number): this {
        if (!this.inputValue) return this;
        if (Validator.isArray(this.inputValue) || Validator.isString(this.inputValue)) {
            if ((this.inputValue as string | unknown[]).length > max)
                this._addError({ errored: this.inputValue, code: "MaxReachedS", label: errorMsg[this.locale].max(String(max)) });
        } else if (Validator.isNumber(this.inputValue)) {
            if ((this.inputValue as number) > max)
                this._addError({ errored: this.inputValue, code: "MaxReachedN", label: errorMsg[this.locale].max(String(max)) });
        }
        return this;
    }

    public isEmail(): this {
        if (!this.inputValue) return this;
        if (!/.+@.+\..+/.test(String(this.inputValue)))
            this._addError({ errored: this.inputValue, code: "InvalidEmail", label: errorMsg[this.locale].isEmail });
        return this;
    }

    public required(): this {
        if (this.inputValue === "" || this.inputValue === null || this.inputValue === undefined)
            this._addError({ errored: this.inputValue, code: "REQUIRED", label: errorMsg[this.locale].required });
        return this;
    }

    public alphanumeric(): this {
        if (!this.inputValue) throw new Error("Please provide a value");
        if (!/^[0-9a-zA-Z]+$/.test(String(this.inputValue)))
            this._addError({ errored: this.inputValue, code: "ALPHANUM", label: errorMsg[this.locale].alphanumeric });
        return this;
    }

    public noSpecials(space: boolean = true, extendJpn: boolean | null = false): this {
        if (!this.inputValue) return this;
        let format = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
        if (!space) format = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
        if (extendJpn) format = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~｀！＠＃＄％＾＆＊（）_＋ー＝「」『』；'："＼｜、。＜＞・？〜]/;
        if (!space && extendJpn) format = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~　｀！＠＃＄％＾＆＊（）_＋ー＝「」『』；'："＼｜、。＜＞・？〜]/;
        if (format.test(String(this.inputValue)))
            this._addError({ errored: this.inputValue, code: "SPESYMBOL", label: errorMsg[this.locale].noSpecials });
        return this;
    }

    public isUUIDv4(): this {
        if (!this.inputValue) throw new Error("Please provide a value");
        if (!/^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(String(this.inputValue)))
            this._addError({ errored: this.inputValue, code: "INVALID_UUIDV4", label: errorMsg[this.locale].isUUIDv4 });
        return this;
    }

    public isSameAs(value: unknown): this {
        if (!this.inputValue) throw new Error("Please provide a value");
        if (this.inputValue !== value)
            this._addError({ errored: this.inputValue, code: "NOT_SAME", label: errorMsg[this.locale].isSameAs });
        return this;
    }

    public verify(returnErrors: boolean = false): boolean | Record<string, ValidationError[]> {
        const hasErrors = Object.values(this.errorMap).some((errs) => errs.length > 0);
        if (hasErrors) return returnErrors ? this.errorMap : false;
        return true;
    }

    public hasError(): boolean {
        return Object.values(this.errorMap).some((errs) => errs.length > 0);
    }

    public reset(): void {
        this.inputValue = null;
        this.errorMap = {};
        this.currentErrorName = "";
        this.pristineMap = {};
        this.valueMap = {};
    }
}

export const validator = new Validator();
